// src/config/modalComponentRegistry.js
import { defineAsyncComponent } from 'vue'
import { isChunkLoadError } from '@/utils/chunkLoadRecovery'
import { useToast } from '@/composables/useToast'

/* Async wrapper with retry. A missing chunk is not retried: the tab predates a hosting deploy and only
   the new build has this modal (docs/bugs/stale_tab_missing_chunk_BUG.md), so say so instead. */
function lazy(importer) {
  return defineAsyncComponent({
    loader: importer,
    suspensible: false,
    timeout: 30000,
    onError(err, retry, fail, attempts) {
      if (isChunkLoadError(err)) {
        useToast().warning('A newer version of the CRM is out and this window needs it. Refresh the page to open it.', {
          timeout: false,
        })
        return fail(err)
      }
      attempts <= 1 ? retry() : fail(err)
    },
  })
}

/* --- Registry --- */
const REGISTRY = {
  PhaseEconomicsReviewModal: lazy(
    () => import('@/components/CustomerCommandCenter/Modals/PhaseEconomicsReviewModal.vue')
  ),
  HouseholdForm: lazy(
    () => import('@/components/CustomerCommandCenter/Modals/HouseholdFormModal.vue')
  ),
  TreeMitigation: lazy(
    () => import('@/components/CustomerCommandCenter/Modals/TreeRemovalModal.vue')
  ),
  SiteSurveyModal: lazy(
    () => import('@/components/CustomerCommandCenter/Modals/SiteSurveyModal.vue')
  ),
  // New Kickbacks Modal
  LogKickbacksModal: lazy(
    () => import('@/components/CustomerCommandCenter/Modals/LogKickbacksModal.vue')
  ),
  // Ready for Install Phase 4 — parse the approved engineering plan set and review what changed before it is
  // applied (standalone: it renders its own review overlay).
  ParseEngineeringPlanModal: lazy(
    () => import('@/components/CustomerCommandCenter/Modals/ParseEngineeringPlanModal.vue')
  ),
  // Electrical Service at Site Survey Review (docs/features/interconnection_decision_FEATURE.md, 2026-10-18): the
  // panel facts, the breaker / line-side tap / upgrade recommendation and the decision (wrapped in GenericModal).
  InterconnectionAssessmentModal: lazy(
    () => import('@/components/CustomerCommandCenter/Modals/InterconnectionAssessmentModal.vue')
  ),
  // eBOS overrides (2026-09-18) — set the eight variables that size the eBOS package by hand, from the customer's
  // page rather than the kit. The same panel the kitting order modal carries.
  EbosOverridesModal: lazy(
    () => import('@/components/CustomerCommandCenter/Modals/EbosOverridesModal.vue')
  ),
  // Phase 60 — sales-rep manual resolve for the "Resolve Referrer Match" task
  ResolveReferrerMatchModal: lazy(
    () => import('@/components/CustomerCommandCenter/Modals/ResolveReferrerMatchModal.vue')
  ),
  // Phase 69H — cycle history viewer for recurring workflow tasks.
  TaskHistoryModal: lazy(
    () => import('@/components/CustomerCommandCenter/Tasks/TaskHistoryModal.vue')
  ),
  // Dynamic Scheduling — staff enter the inspection windows Elevate provided; the
  // customer picks one. No availability search, no crew hold (see
  // functions/scheduling/offerProvidedWindows.js).
  ElevateInspectionWindowsModal: lazy(
    () => import('@/components/CustomerCommandCenter/Modals/ElevateInspectionWindowsModal.vue')
  ),
  // Lead-stage "Generate Estimate (No Design)" (2026-08-06) — converts a typeless
  // lead: pre-flight inputs → product pick → engine run → type assignment.
  GenerateLeadEstimateModal: lazy(
    () => import('@/components/CustomerCommandCenter/Modals/GenerateLeadEstimateModal.vue')
  ),
  // Design proposal flow (2026-09-29) — send a customer their design proposal (Send Proposal to Customer) or their
  // estimate (Estimate Sent): see the exact email and what is missing, send or skip. `kind` comes from the action's
  // modalProps (docs/features/design_proposal_flow_FEATURE.md §3, §6).
  SendToCustomerModal: lazy(
    () => import('@/components/CustomerCommandCenter/Modals/SendToCustomerModal.vue')
  ),
  // Generate Estimate (same feature, §4): the roof-average estimate with a simpler face, from the Estimate Sent row
  // and a Lead Dashboard row. Replaces GenerateLeadEstimateModal once the old task is unplaced.
  GenerateEstimateModal: lazy(
    () => import('@/components/CustomerCommandCenter/Modals/GenerateEstimateModal.vue')
  ),
}

const ALIASES = {
  KickbacksModal: 'LogKickbacksModal',
}

/* --- Optional FailSafe for debugging (never auto-used by store) --- */
const FailSafeModal = {
  name: 'FailSafeModal',
  props: { missingKey: { type: String, default: '' } },
  emits: ['close'],
  template: `
    <div class="p-4">
      <div class="text-sm text-red-700 font-medium mb-2">Modal component not found</div>
      <div class="text-xs text-gray-600 mb-4">Key: <code>{{ missingKey || '(none)' }}</code></div>
      <button class="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700" @click="$emit('close')">Close</button>
    </div>
  `,
}

export function resolveModalComponent(key, { fallback = false } = {}) {
  if (!key) return fallback ? FailSafeModal : null
  const canonical = ALIASES[key] || key
  return REGISTRY[canonical] || (fallback ? FailSafeModal : null)
}

export function hasModalKey(key) {
  if (!key) return false
  const canonical = ALIASES[key] || key
  return !!REGISTRY[canonical]
}

export async function preloadModal(key) {
  const comp = resolveModalComponent(key)
  const loader = comp && comp.__asyncLoader
  if (typeof loader === 'function') {
    try {
      await loader()
    } catch {
      // ignore — best-effort preload, modal will load on first open if this fails
    }
  }
  return !!comp
}

export const MODAL_COMPONENT_REGISTRY = REGISTRY
